export const DEFAULT_DESTINATARIO_FATTURAZIONE = {
  abilitato: false,
  tipo: "familiare",
  contatto_familiare_indice: 0,
  nome: "",
  cognome: "",
  codice_fiscale: "",
  partita_iva: "",
  pec: "",
  codice_sdi: "",
  email: "",
  telefono: "",
  indirizzo: "",
  cap: "",
  citta: "",
  provincia: "",
};

/** Placeholder tutti zeri (anche lunghezza errata) → assente (0000000, invio SDI B2C standard). */
export function normalizeCodiceSdi(raw) {
  const s = String(raw ?? "").trim().toUpperCase();
  if (!s || s === "0000000" || s === "XXXXXXX") return "0000000";
  if (/^0+$/.test(s)) return "0000000";
  return s;
}

export function normalizeDestinatarioFatturazione(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...DEFAULT_DESTINATARIO_FATTURAZIONE };
  }
  return {
    ...DEFAULT_DESTINATARIO_FATTURAZIONE,
    ...raw,
    abilitato: !!raw.abilitato,
    tipo: raw.tipo === "altro" ? "altro" : "familiare",
    contatto_familiare_indice: Number.isFinite(Number(raw.contatto_familiare_indice))
      ? Number(raw.contatto_familiare_indice)
      : 0,
  };
}

/** Dati familiare da contatto anagrafica + amministrativa.familiare. */
export function familiareBaseDaFascicolo(anagrafica = {}, amministrativa = {}, indice = 0) {
  const contatti = anagrafica?.contatti_familiari || [];
  const idx = Math.max(0, Math.min(indice, Math.max(contatti.length - 1, 0)));
  const contatto = contatti[idx] || {};
  const fam = amministrativa?.familiare || {};

  const nomeContatto = (contatto.nome || "").trim();
  let nome = (fam.nome || "").trim();
  let cognome = (fam.cognome || "").trim();
  if (nomeContatto && !nome && !cognome) {
    const parts = nomeContatto.split(/\s+/);
    nome = parts[0] || "";
    cognome = parts.slice(1).join(" ") || "";
  }

  const nomeCompleto =
    nomeContatto || [nome, cognome].filter(Boolean).join(" ").trim() || [cognome, nome].filter(Boolean).join(" ");

  return {
    nome,
    cognome,
    nome_completo: nomeCompleto,
    codice_fiscale: fam.codice_fiscale || fam.cf || contatto.codice_fiscale || "",
    email: contatto.email || fam.email || "",
    telefono: contatto.telefono || fam.telefono || "",
    pec: fam.pec || amministrativa?.pec || "",
    codice_sdi: fam.codice_sdi || amministrativa?.codice_sdi || "",
    indirizzo: fam.indirizzo || "",
    cap: fam.cap || "",
    citta: fam.citta || "",
    provincia: fam.provincia || "",
    relazione: contatto.relazione || fam.relazione || "",
  };
}

/** Valori effettivi mostrati/salvati (config sovrascrive base familiare). */
export function mergeDestinatarioEffettivo(config, anagrafica, amministrativa) {
  const cfg = normalizeDestinatarioFatturazione(config);
  const base =
    cfg.tipo === "familiare"
      ? familiareBaseDaFascicolo(anagrafica, amministrativa, cfg.contatto_familiare_indice)
      : {};

  const pick = (...keys) => {
    for (const key of keys) {
      const v = cfg[key];
      if (v != null && String(v).trim()) return String(v).trim();
      const b = base[key];
      if (b != null && String(b).trim()) return String(b).trim();
    }
    return "";
  };

  const nome = pick("nome");
  const cognome = pick("cognome");
  const nomeCompleto =
    pick("nome_completo") || [nome, cognome].filter(Boolean).join(" ") || base.nome_completo || "";

  return {
    ...cfg,
    nome,
    cognome,
    nome_completo: nomeCompleto,
    codice_fiscale: pick("codice_fiscale"),
    partita_iva: pick("partita_iva"),
    pec: pick("pec"),
    codice_sdi: normalizeCodiceSdi(pick("codice_sdi")),
    email: pick("email"),
    telefono: pick("telefono"),
    indirizzo: pick("indirizzo"),
    cap: pick("cap"),
    citta: pick("citta"),
    provincia: pick("provincia"),
  };
}

/** Pre-compilazione form generazione fattura. */
export function destinatarioPerGeneraFattura(amministrativa, anagrafica) {
  const cfg = normalizeDestinatarioFatturazione(amministrativa?.destinatario_fatturazione);
  if (!cfg.abilitato) return null;

  const eff = mergeDestinatarioEffettivo(cfg, anagrafica, amministrativa);
  return {
    destinatario_codice_fiscale: eff.codice_fiscale || "",
    destinatario_partita_iva: eff.partita_iva || "",
    destinatario_pec: eff.pec || "",
    destinatario_sdi: normalizeCodiceSdi(eff.codice_sdi),
    destinatario_email: eff.email || "",
    destinatario_indirizzo: eff.indirizzo || "",
    destinatario_cap: eff.cap || "",
    destinatario_citta: eff.citta || "",
    destinatario_provincia: eff.provincia || "",
  };
}

export function prefillDestinatarioFamiliare(config, anagrafica, amministrativa) {
  const cfg = normalizeDestinatarioFatturazione(config);
  const base = familiareBaseDaFascicolo(anagrafica, amministrativa, cfg.contatto_familiare_indice);
  return {
    ...cfg,
    nome: base.nome,
    cognome: base.cognome,
    codice_fiscale: base.codice_fiscale,
    email: base.email,
    telefono: base.telefono,
    pec: base.pec,
    codice_sdi: base.codice_sdi || cfg.codice_sdi,
    indirizzo: base.indirizzo,
    cap: base.cap,
    citta: base.citta,
    provincia: base.provincia,
  };
}

/**
 * CF da usare in ricerca FIC / collegamento: intestatario se abilitato,
 * altrimenti familiare, altrimenti ospite.
 */
export function cfIntestatarioDaFascicolo(fascicolo) {
  if (!fascicolo || typeof fascicolo !== "object") return "";
  const anag = fascicolo.anagrafica || {};
  const amm = fascicolo.amministrativa || {};
  const cfg = normalizeDestinatarioFatturazione(amm.destinatario_fatturazione);
  if (cfg.abilitato) {
    const eff = mergeDestinatarioEffettivo(cfg, anag, amm);
    const cfDest = String(eff.codice_fiscale || "").trim().toUpperCase();
    if (cfDest.length >= 11) return cfDest;
  }
  const fam = familiareBaseDaFascicolo(anag, amm, 0);
  const cfFam = String(fam.codice_fiscale || "").trim().toUpperCase();
  if (cfFam.length >= 11) return cfFam;
  return String(anag.codice_fiscale || "").trim().toUpperCase();
}
